import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import {
  PaymentService,
  PaymentPlan,
} from "../../api/payment";
import { getAllPatients } from "../../api/patient";

type PatientOption = { id: string; name: string };

const PaymentList: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [selectedPatientName, setSelectedPatientName] = useState<string>("");
  const [plans, setPlans] = useState<PaymentPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Load all patients for dropdown
  useEffect(() => {
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        const normalized: PatientOption[] = (Array.isArray(list) ? list : [])
          .map((p: any): PatientOption => {
            const id = p.id || p._id || p.patientId || p.value || "";
            const name = p.name || p.fullName || p.label || "";
            return { id, name };
          })
          .filter((p) => p.id && p.name);
        setPatients(normalized);
      } catch (err: any) {
        setPatients([]);
        toast.error(
          err?.response?.data?.message || "Failed to load patients for payments"
        );
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  const handlePatientChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;
    setSelectedPatientId(value);
    const patient = patients.find((p) => p.id === value);
    setSelectedPatientName(patient?.name || "");

    if (!value) {
      setPlans([]);
      return;
    }

    setLoadingPlans(true);
    try {
      const res = await PaymentService.getPlansByPatient(value);
      const raw = res?.data?.data || res?.data || [];
      const list: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw.items)
          ? raw.items
          : [];

      if (!list.length) {
        setPlans([]);
        return;
      }

      const normalizedPlans: PaymentPlan[] = list.map((item: any) => ({
        id: item.planId || item.id || "",
        planId: item.planId || item.id,
        patientId: item.patientId,
        treatmentId: item.treatmentId,
        treatmentPlanId: item.treatmentPlanId,
        treatmentCost: Number(item.treatmentCost) || 0,
        discount: Number(item.discount) || 0,
        additionalCharges: Number(item.additionalCharges) || 0,
        reasonForExtraCharge: item.reasonForExtraCharge,
        totalPayable: Number(item.totalPayable) || undefined,
        totalReceived: Number(item.totalReceived) || undefined,
        pendingAmount:
          Number(
            item.pendingAmount !== undefined
              ? item.pendingAmount
              : item.remainingAmount
          ) || undefined,
        remainingAmount:
          item.remainingAmount !== undefined
            ? Number(item.remainingAmount)
            : undefined,
        status: item.status,
        transactions: [],
      }));

      // If there is exactly one plan, jump directly to its transaction screen
      if (normalizedPlans.length === 1) {
        const single = normalizedPlans[0];

        navigate(`/payment/plan/${single.id || single.planId}`, {
          replace: true,
        });
        return;
      }

      setPlans(normalizedPlans);
    } catch (err: any) {
      setPlans([]);
      toast.error(
        err?.response?.data?.message ||
        "Failed to load payment plans for selected patient"
      );
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleCreatePlan = () => {
    if (selectedPatientId) {
      navigate(`/payment/create/${selectedPatientId}`);
    } else {
      navigate("/payment/create");
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <div className="flex-1 overflow-y-auto p-6">
          {(loading || patientsLoading || loadingPlans) && <Loader />}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="max-w-6xl mx-auto"
          >
            <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8">
              {/* Header Row */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Payments
                  </h2>
                  <p className="text-sm text-gray-500">
                    Select a patient to view or manage their payment plan.
                  </p>
                </div>
                {/* <button
                  onClick={handleCreatePlan}
                  className="px-5 py-2 rounded-xl bg-[#016683] text-white font-medium hover:opacity-90 transition"
                >
                  Create Payment Plan
                </button> */}
              </div>

              {/* Patient Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient
                </label>
                <div className="flex flex-col md:flex-row gap-3 md:items-center">
                  <select
                    value={selectedPatientId}
                    onChange={handlePatientChange}
                    disabled={patientsLoading}
                    className="mt-1 w-full md:w-80 p-2 border rounded-lg"
                  >
                    {patientsLoading ? (
                      <option value="">Loading patients...</option>
                    ) : (
                      <>
                        <option value="">Select Patient</option>
                        {patients.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>

                  {selectedPatientName && (
                    <span className="text-sm text-gray-600 md:ml-2">
                      Viewing payments for{" "}
                      <span className="font-semibold text-gray-800">
                        {selectedPatientName}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {/* Content Area */}
              {!selectedPatientId ? (
                <div className="py-12 text-center">
                  <div className="mb-4">
                    <svg
                      className="mx-auto h-16 w-16 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Select a patient to view payment plans
                  </h3>
                  <p className="text-gray-500">
                    Once you choose a patient, we&apos;ll show any existing
                    payment plan or help you create a new one.
                  </p>
                </div>
              ) : plans.length === 0 ? (
                <div className="py-10 text-center">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    No Payment Plan Found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    It looks like{" "}
                    <span className="font-semibold">
                      {selectedPatientName || "this patient"}
                    </span>{" "}
                    doesn&apos;t have a payment plan yet.
                  </p>
                  <button
                    onClick={handleCreatePlan}
                    className="px-6 py-2 rounded-lg bg-[#016683] text-white hover:opacity-90 transition"
                  >
                    Create Payment Plan
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  {plans.map((plan) => {
                    const safeTreatmentCost = Number(plan.treatmentCost) || 0;
                    const safeDiscount = Number(plan.discount) || 0;
                    const safeAdditional =
                      Number(plan.additionalCharges) || 0;
                    const total =
                      (plan.totalPayable as number) ||
                      safeTreatmentCost - safeDiscount + safeAdditional;
                    const received =
                      (plan.totalReceived as number) || 0;
                    const pending =
                      plan.pendingAmount !== undefined
                        ? (plan.pendingAmount as number)
                        : total - received;

                    return (
                      <motion.div
                        key={plan.id}
                        whileHover={{ y: -4, scale: 1.01 }}
                        className="border border-gray-200 rounded-2xl p-4 shadow-sm bg-gradient-to-br from-white to-gray-50 flex flex-col justify-between"
                      >
                        <div className="mb-3">
                          <p className="text-xs font-semibold uppercase text-gray-500 tracking-wide">
                            Payment Plan
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Patient:{" "}
                            <span className="font-medium">
                              {selectedPatientName}
                            </span>
                          </p>
                          {plan.status && (
                            <span className="inline-flex mt-2 px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700 font-medium">
                              {plan.status}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                          <div>
                            <p className="text-gray-500">Total Payable</p>
                            <p className="text-base font-semibold text-gray-800">
                              ₹{total.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Received</p>
                            <p className="text-base font-semibold text-green-600">
                              ₹{received.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Pending</p>
                            <p
                              className={`text-base font-semibold ${pending > 0
                                ? "text-red-600"
                                : pending < 0
                                  ? "text-yellow-600"
                                  : "text-gray-700"
                                }`}
                            >
                              ₹{Math.abs(pending).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center mt-2">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/payment/plan/${plan.id || plan.planId}`)
                            }
                            className="text-sm font-medium text-[#016683] hover:underline"
                          >
                            View / Add Transactions
                          </button>
                          <button
                            type="button"
                            onClick={handleCreatePlan}
                            className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100"
                          >
                            New Plan
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PaymentList;